/**
 * Auto-collapse / hide policy driven by panel engagement history.
 *
 * When a new panel opens and visible count exceeds the cap, stale panels
 * are minimized first, then hidden. Recently engaged panels are protected
 * so re-opening chat keeps it on screen.
 */
import type { PanelLayout, PanelId } from '../types';
import type { PanelEngagementSnapshot } from '../stores/panelEngagementStore';

/** Max non-nav panels visible before pruning stale panels via hide. */
export const MAX_VISIBLE_CONTENT_PANELS = 6;

/** When visible count reaches this threshold, auto-organize on each new open. */
export const AUTO_ORGANIZE_VISIBLE_THRESHOLD = 4;

/** Panels engaged within this window are not hidden (may still minimize if over cap). */
export const RECENT_ENGAGEMENT_MS = 45_000;

/** Never auto-hide these panel ids during pruning. */
export const AUTO_HIDE_EXEMPT: ReadonlySet<PanelId> = new Set([
  'nav',
  'settings',
  /** Never auto-hide voice — an active call must keep its panel surface. */
  'voice',
]);

export type PanelStaleAction =
  | { type: 'minimize'; panelId: PanelId }
  | { type: 'hide'; panelId: PanelId };

export interface StalePanelPolicyOptions {
  maxVisible?: number;
  recentEngagementMs?: number;
  now?: number;
}

function isContentPanel(id: string, panel: PanelLayout): boolean {
  if (id === 'nav') return false;
  if (!panel.visible) return false;
  return true;
}

function engagementRank(
  panelId: PanelId,
  snapshot: PanelEngagementSnapshot,
): number {
  const idx = snapshot.stackOrder.indexOf(panelId);
  if (idx === -1) return -1;
  return snapshot.stackOrder.length - 1 - idx;
}

function lastEngaged(
  panelId: PanelId,
  snapshot: PanelEngagementSnapshot,
): number {
  return snapshot.lastEngagedAt[panelId] ?? 0;
}

function isRecentlyEngaged(
  panelId: PanelId,
  snapshot: PanelEngagementSnapshot,
  now: number,
  windowMs: number,
): boolean {
  const ts = lastEngaged(panelId, snapshot);
  return ts > 0 && now - ts <= windowMs;
}

function sortStaleCandidates(
  ids: PanelId[],
  snapshot: PanelEngagementSnapshot,
): PanelId[] {
  return [...ids].sort((a, b) => {
    const rankA = engagementRank(a, snapshot);
    const rankB = engagementRank(b, snapshot);
    if (rankA !== rankB) return rankA - rankB;
    return lastEngaged(a, snapshot) - lastEngaged(b, snapshot);
  });
}

/**
 * Decide which panels to minimize/hide when `openingPanelId` is shown.
 * Returns actions in apply order (minimize before hide).
 */
export function computeStalePanelActions(
  panels: Record<string, PanelLayout>,
  openingPanelId: PanelId,
  snapshot: PanelEngagementSnapshot,
  options: StalePanelPolicyOptions = {},
): PanelStaleAction[] {
  const maxVisible = options.maxVisible ?? MAX_VISIBLE_CONTENT_PANELS;
  const recentMs = options.recentEngagementMs ?? RECENT_ENGAGEMENT_MS;
  const now = options.now ?? Date.now();

  const visibleContent = Object.entries(panels)
    .filter(([id, p]) => isContentPanel(id, p))
    .map(([id]) => id as PanelId);

  const projectedVisible = new Set(visibleContent);
  projectedVisible.add(openingPanelId);

  if (projectedVisible.size <= maxVisible) {
    return [];
  }

  const staleCandidates = sortStaleCandidates(
    [...projectedVisible].filter(
      (id) => id !== openingPanelId && !AUTO_HIDE_EXEMPT.has(id),
    ),
    snapshot,
  );

  const actions: PanelStaleAction[] = [];
  let visibleCount = projectedVisible.size;

  // Prefer hide over minimize so opening many panels stays organized (no thin header bars).
  for (const panelId of staleCandidates) {
    if (visibleCount <= maxVisible) break;
    const panel = panels[panelId];
    if (!panel?.visible) continue;
    if (isRecentlyEngaged(panelId, snapshot, now, recentMs)) {
      continue;
    }
    actions.push({ type: 'hide', panelId });
    visibleCount -= 1;
  }

  return actions;
}
